import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, ShieldCheck, Sparkles, Calculator, Handshake } from "lucide-react";

const TOOLS = [
  {
    id: "marketplace",
    href: "/marketplace",
    external: false,
    title: "Partner Marketplace",
    blurb: "Vetted partners for what comes after credit repair — personal loans, business funding, mortgage referrals, auto financing, credit monitoring, insurance, and tax services.",
    eta: "7 categories · direct intros",
    icon: Handshake,
  },
  {
    id: "pfs",
    href: "/pfs-tool.html",
    external: true,
    title: "Personal Financial Statement Builder",
    blurb: "Answer a few questions and we'll generate a polished, bank-ready PFS you can print or save — perfect for lenders, investors, or your own records.",
    eta: "~5 minutes · lender-ready output",
    icon: FileText,
  },
  {
    id: "dti",
    href: "/dti-tool.html",
    external: true,
    title: "Debt-to-Income Calculator",
    blurb: "See your front-end and back-end DTI ratios instantly — the same two numbers every mortgage lender checks first. Color-coded against FHA & conventional thresholds.",
    eta: "~2 minutes · instant results",
    icon: Calculator,
  },
];

export default function Tools() {
  return (
    <section className="section" data-testid="tools-page">
      <div className="container-if">
        <div className="max-w-2xl mb-12">
          <div className="font-mono-label text-[var(--if-gold)] mb-3">Free Tools</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
            Practical tools, <span className="italic text-[var(--if-gold)]">no fluff.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#1B211A]/70" style={{ fontFamily: "Inter, sans-serif" }}>
            We built these because we got tired of seeing people guess. Use them freely —
            and if you want a real human to walk through the numbers with you,
            <Link to="/booking" className="text-[var(--if-gold)] underline ml-1">book a free strategy call</Link>.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const CardInner = (
              <>
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-[var(--if-gold)]/8 rounded-full blur-3xl pointer-events-none transition-opacity group-hover:opacity-150" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/30 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[var(--if-gold)]" />
                  </div>
                  <h2 className="font-display text-2xl text-white mb-3">{t.title}</h2>
                  <p className="text-sm text-white/65 mb-5 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
                    {t.blurb}
                  </p>
                  <div className="flex justify-between items-center pt-4 border-t border-white/8">
                    <span className="text-[11px] text-white/45 uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>
                      {t.eta}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[var(--if-gold)] text-sm font-medium" style={{ fontFamily: "Inter, sans-serif" }}>
                      Start <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </>
            );
            const className = "card-dark p-7 hover:border-[var(--if-gold)]/40 transition-all group relative overflow-hidden";
            return t.external ? (
              <a
                key={t.id}
                href={t.href}
                className={className}
                data-testid={`tool-card-${t.id}`}
              >
                {CardInner}
              </a>
            ) : (
              <Link
                key={t.id}
                to={t.href}
                className={className}
                data-testid={`tool-card-${t.id}`}
              >
                {CardInner}
              </Link>
            );
          })}

          {/* Coming-soon placeholder slot */}
          <div className="card-dark p-7 opacity-60 border-dashed">
            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center mb-5">
              <Sparkles className="w-5 h-5 text-white/40" />
            </div>
            <h2 className="font-display text-2xl text-white/60 mb-3">More tools coming</h2>
            <p className="text-sm text-white/40 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
              DTI calculator, mortgage pre-qualification estimator, dispute letter generator, and more.
              Have something specific you'd like to see? <Link to="/contact" className="text-[var(--if-gold)] underline">Let us know.</Link>
            </p>
          </div>
        </div>

        <div className="mt-14 card-dark p-7 max-w-2xl border-[var(--if-gold)]/20" data-testid="tools-trust-note">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[var(--if-gold)] mt-0.5 shrink-0" />
            <div>
              <h3 className="font-display text-lg text-white mb-2">Your data stays yours.</h3>
              <p className="text-sm text-white/60 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
                Tools run on our secure servers. We don't sell your information. If you optionally share
                your email, we'll only use it to send the document you requested and follow up once.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
